import React from 'react';
import { useTranslation } from 'react-i18next';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../i18n';
import { useDirection } from '../../hooks/useDirection';

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'button' | 'minimal';
  className?: string;
}

const LANGUAGE_NAMES: Record<SupportedLanguage, { native: string; english: string; short: string; flag: string }> = {
  en: {
    native: 'English',
    english: 'English',
    short: 'En',
    flag: '🇺🇸'
  },
  ar: {
    native: 'العربية',
    english: 'Arabic',
    short: 'Ar',
    flag: '🇸🇦'
  }
};

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'dropdown',
  className = ''
}) => {
  const { i18n, t } = useTranslation();
  const { direction, isRTL } = useDirection();
  const [isOpen, setIsOpen] = React.useState(false);

  const currentLanguage = i18n.language as SupportedLanguage;
  const currentLangInfo = LANGUAGE_NAMES[currentLanguage];

  const handleLanguageChange = async (language: SupportedLanguage) => {
    try {
      await i18n.changeLanguage(language);
      setIsOpen(false);
      
      // Store the selected language in localStorage
      localStorage.setItem('i18nextLng', language);
      
      // Reload the page to ensure all components re-render with new language
      // This is needed because some components might not be subscribed to i18n changes
      window.location.reload();
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  if (variant === 'button') {
    return (
      <button
        onClick={() => handleLanguageChange(currentLanguage === 'en' ? 'ar' : 'en')}
        className={`
          inline-flex items-center gap-2 px-3 py-2 text-sm font-medium 
          rounded-md border border-gray-300 bg-white hover:bg-gray-50 
          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
          transition-colors duration-200
          ${isRTL ? 'flex-row-reverse' : ''}
          ${className}
        `}
        title={t('common.navigation.language_switch', 'Switch Language')}
      >
        <GlobeAltIcon className="w-4 h-4" />
        <span className="hidden sm:inline">
          {currentLangInfo.short}
        </span>
        <span className="sm:hidden">
          {currentLangInfo.short}
        </span>
      </button>
    );
  }

  if (variant === 'minimal') {
    return (
      <button
        onClick={() => handleLanguageChange(currentLanguage === 'en' ? 'ar' : 'en')}
        className={`
          text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200
          ${className}
        `}
        title={t('common.navigation.language_switch', 'Switch Language')}
      >
        {currentLangInfo.short}
      </button>
    );
  }

  // Default dropdown variant
  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          inline-flex items-center gap-2 px-3 py-2 text-sm font-medium 
          rounded-md border border-gray-300 bg-white hover:bg-gray-50 
          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
          transition-colors duration-200
          ${isRTL ? 'flex-row-reverse' : ''}
        `}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <GlobeAltIcon className="w-4 h-4" />
        <span className="hidden sm:inline">
          {currentLangInfo.short}
        </span>
        <span className="sm:hidden">
          {currentLangInfo.short}
        </span>
        <svg 
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown menu */}
          <div 
            className={`
              absolute z-20 mt-1 w-48 bg-white border border-gray-200 
              rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5
              ${isRTL ? 'left-0' : 'right-0'}
            `}
            dir={direction}
          >
            {SUPPORTED_LANGUAGES.map((lang) => {
              const langInfo = LANGUAGE_NAMES[lang];
              const isActive = lang === currentLanguage;
              
              return (
                <button
                  key={lang}
                  onClick={() => handleLanguageChange(lang)}
                  className={`
                    w-full text-left px-4 py-2 text-sm hover:bg-gray-100 
                    transition-colors duration-200 flex items-center gap-3
                    ${isActive ? 'bg-primary-lightest text-primary-dark' : 'text-gray-700'}
                    ${isRTL ? 'text-right flex-row-reverse' : ''}
                  `}
                  disabled={isActive}
                >
                  <div className={`flex flex-col ${isRTL ? 'items-end' : 'items-start'}`}>
                    <span className="font-medium">{langInfo.short}</span>
                  </div>
                  {isActive && (
                    <svg 
                      className={`w-4 h-4 text-primary ${isRTL ? 'mr-auto' : 'ml-auto'}`}
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path 
                        fillRule="evenodd" 
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                        clipRule="evenodd" 
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSwitcher;